import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "nxtconstants";

const HeroSection = ({ heroData = {} }) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const openVideo = () => {
    setIsVideoOpen(true);
  };

  const closeVideo = () => {
    setIsVideoOpen(false);
  };

  return (
    <Section>
      <Container>
        <ContentWrapper>
          <TextContent>
            <MainHeading>
              {heroData.titleLines && heroData.titleLines.map((value, index) => (
                <React.Fragment key={index}>
                  {value}
                  {index < heroData.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </MainHeading>
            <Description>
              {heroData.description}
            </Description>
            <ButtonGroup>
              <CTAButton href="#register">
                <PlayIcon src={heroData.playIcon} alt="Play Icon" width={24} height={24} />
                {heroData.ctaText}
              </CTAButton>
              <TalkToUsButton onClick={openVideo}>
                <ChatIcon
                  src={heroData.chatIcon}
                  alt="Chat Icon"
                  width={24}
                  height={24}
                />
                {heroData.demoText}
              </TalkToUsButton>
            </ButtonGroup>
          </TextContent>

          <VisualContent>
            <HeroImage
              src={heroData.heroImage}
              alt="Wealthy Partner Dashboard - Mutual Fund Distribution Platform Interface"
              loading="eager"
              fetchPriority="high"
              width={1280}
              height={1200}
            />
          </VisualContent>
        </ContentWrapper>
      </Container>

      {isVideoOpen && (
        <VideoModal>
          <ModalOverlay onClick={closeVideo} />
          <ModalContent>
            <CloseButton onClick={closeVideo}>×</CloseButton>
            <VideoContainer>
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${heroData.videoId}?autoplay=1`}
                title="Wealthy Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="eager"
              ></iframe>
            </VideoContainer>
          </ModalContent>
        </VideoModal>
      )}
    </Section>
  );
};

const Section = styled.section`
  width: 100%;
  margin-bottom: 7rem;
  background: #F8F8F8;
  display: flex;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: auto;
    background: ${colors.white};
    margin-bottom: 5.6rem;
  }
`;

const Container = styled.div`
  width: 92%;
  margin-left: auto;
  @media (max-width: 768px) {
    width: 90%;
    margin: 0 auto;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  justify-content: space-between;
  min-height: 40rem;

  @media (max-width: 768px) {
    text-align: center;
    order: 1;
    margin-left:0;
    min-height: auto;
    gap: 3rem;
    padding: 2rem 0;
  }
`;

const MainHeading = styled.h1`
  font-family: 'DM Serif Display', serif;
  font-size: 5rem;
  font-weight: 400;
  color: ${colors.textBlack};
  line-height: 1.2;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 3.4rem;
    line-height: 1.3;
    width: 100%;
    margin: 0 auto;
  }
`;

const Description = styled.p`
  font-family: 'Lato';
  font-size: 2rem;
  font-weight: 700;
  color: ${colors.textGray};
  line-height: 1.6;
  margin: 0;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 1.6rem;
    font-weight: 400;
    font-family: 'Lato';
    line-height: 21px;
    width: 85%;
    margin: 0 auto;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: auto;

  @media (max-width: 768px) {
    justify-content: center;
    flex-direction: row;
    align-items: center;
    margin-top: 2rem;
    gap: 1rem;
  }
`;

const CTAButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${colors.primary};
  color: white;
  border: none;
  border-radius: 5rem;
  padding: 1rem 4rem;
  font-family: 'Lato';
  font-size: 1.8rem;
  font-weight: 700;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;

  @media (max-width: 768px) {
    padding: 1rem 4.5rem;
    font-size: 1.4rem;
  }
`;

const TalkToUsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 4rem;
  background: white;
  border: none;
  color: ${colors.primary};
  border-radius: 5rem;
  font-family: 'Lato';
  font-size: 1.8rem;
  font-weight: 700;
  justify-content: center;
  cursor: pointer;

  @media (max-width: 768px) {
    border: solid;
    padding: 1rem 4rem;
    font-size: 1.4rem;
  }
`;

const PlayIcon = styled.img`
  width: 2rem;
  height: 2rem;

  @media( max-width: 768px){
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const ChatIcon = styled.img`
  width: 2rem;
  height: 2rem;

  @media( max-width: 768px){
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const VisualContent = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  margin-top: 1rem;
  height: auto;
  aspect-ratio: 1280 / 1200;
  border-radius: 2rem;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const VideoModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  cursor: pointer;
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 80rem;
  background: white;
  border-radius: 1.2rem;
  overflow: hidden;
  z-index: 1001;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  font-size: 2rem;
  cursor: pointer;
  z-index: 1002;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; 

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

export default HeroSection;